import {
  createContext,
  useCallback,
  useRef,
  type FC,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { SCROLL_ANCHOR_SLACK } from "./useScrollAnchorOnResize";

export interface ScrollContextType {
  scrollRef: MutableRefObject<HTMLDivElement | null>;
  /** Pins the message list to its bottom and drops any reading anchor. */
  scrollToBottom: () => void;
  /**
   * Pins the bottom only when the reader was already there (within
   * `SCROLL_ANCHOR_SLACK`) before `appendedHeight` pixels of new content
   * arrived below. Content that appends itself after a long answer — option
   * buttons, the rating stars — must not yank a reader who is still at the
   * top of that answer (LAS-1381). The caller passes its own rendered height
   * because by the time it can scroll, the list's scrollHeight already
   * includes it.
   */
  scrollToBottomIfNearBottom: (appendedHeight?: number) => void;
  /**
   * Scrolls so the message's top sits at the top of the message area and
   * installs it as the reading anchor that `keepAnchor` maintains. Returns
   * false when the message has no `data-message-id` element in the list
   * (embedder templates may drop it); callers then fall back to the bottom.
   */
  anchorToMessage: (messageId: number) => boolean;
  /**
   * Called as streamed content grows. With a reading anchor installed it
   * re-applies the anchor, so the question climbs to the top once there is
   * enough content below it to scroll into — and lets go for good the moment
   * the scroll position differs from the one the last anchoring left behind,
   * because that difference is the reader scrolling. Without an anchor (the
   * list chose the bottom pin for this message) it follows the bottom as the
   * list always did.
   */
  keepAnchor: () => void;
  /** Whether a reading anchor is installed and the reader has not scrolled away from it. */
  hasReadingAnchor: () => boolean;
  /**
   * What a panel resize re-pins: the reading anchor while one is installed,
   * the bottom otherwise. Re-pinning the bottom while an answer is anchored
   * would silently drop the anchor mid-stream.
   */
  restoreReadingPosition: () => void;
}

export const ScrollContext = createContext<ScrollContextType | null>(null);

/** The attribute message bubbles carry so the list can scroll to one of them. */
export const messageAnchorSelector = (messageId: number): string => `[data-message-id="${messageId}"]`;

interface ReadingAnchor {
  messageId: number;
  /** `scrollTop` as the last anchoring left it, after the browser's clamping. */
  pinnedScrollTop: number;
}

/**
 * The anchor's lifecycle for the message currently streaming in:
 * - `none`: the list pinned the bottom for it, so growth follows the bottom;
 * - an anchor: growth re-applies it;
 * - `released`: the reader scrolled while it was anchored, so growth leaves
 *   the scroll position alone.
 */
type AnchorState = ReadingAnchor | "none" | "released";

/**
 * Places the target's top where the list's first message sits at scrollTop 0,
 * i.e. just below the container's own top padding. The browser clamps the
 * assignment when there is not yet enough content below the target, which is
 * exactly why `keepAnchor` re-applies it while an answer grows.
 */
const applyAnchor = (container: HTMLDivElement, messageId: number): ReadingAnchor | null => {
  const target = container.querySelector<HTMLElement>(messageAnchorSelector(messageId));
  if (target === null) return null;
  const targetOffset =
    target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
  const paddingTop = parseFloat(window.getComputedStyle(container).paddingTop) || 0;
  container.scrollTop = Math.max(0, targetOffset - paddingTop);
  return { messageId, pinnedScrollTop: container.scrollTop };
};

const isAnchor = (state: AnchorState): state is ReadingAnchor => typeof state !== "string";

interface ScrollProviderProps {
  children: ReactNode;
}

export const ScrollProvider: FC<ScrollProviderProps> = ({ children }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<AnchorState>("none");

  const scrollToBottom = useCallback(() => {
    anchorRef.current = "none";
    const container = scrollRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const scrollToBottomIfNearBottom = useCallback((appendedHeight = 0) => {
    const container = scrollRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight - appendedHeight;
    if (distanceFromBottom > SCROLL_ANCHOR_SLACK) return;
    scrollToBottom();
  }, [scrollToBottom]);

  const anchorToMessage = useCallback((messageId: number): boolean => {
    const container = scrollRef.current;
    if (!container) return false;
    const anchor = applyAnchor(container, messageId);
    anchorRef.current = anchor ?? "none";
    return anchor !== null;
  }, []);

  /**
   * The anchor state as of now: an installed anchor whose scroll position no
   * longer matches the one the last anchoring left behind has been scrolled
   * away from by the reader, so it becomes `released` before anyone acts on
   * it. Every reader of the state goes through here, so a scroll that
   * happened between two typewriter ticks (or before a resize) counts too.
   */
  const settledAnchor = useCallback((container: HTMLDivElement): AnchorState => {
    const state = anchorRef.current;
    if (isAnchor(state) && container.scrollTop !== state.pinnedScrollTop) {
      anchorRef.current = "released";
    }
    return anchorRef.current;
  }, []);

  const hasReadingAnchor = useCallback((): boolean => {
    const container = scrollRef.current;
    return container !== null && isAnchor(settledAnchor(container));
  }, [settledAnchor]);

  /** Re-applies the installed anchor; drops to `released` if its bubble is gone. */
  const reapplyAnchor = useCallback((container: HTMLDivElement, anchor: ReadingAnchor): void => {
    anchorRef.current = applyAnchor(container, anchor.messageId) ?? "released";
  }, []);

  const keepAnchor = useCallback(() => {
    const container = scrollRef.current;
    if (container === null) return;
    const state = settledAnchor(container);
    if (state === "none") {
      container.scrollTop = container.scrollHeight;
      return;
    }
    if (state === "released") return;
    reapplyAnchor(container, state);
  }, [reapplyAnchor, settledAnchor]);

  const restoreReadingPosition = useCallback(() => {
    const container = scrollRef.current;
    if (container !== null) {
      const state = settledAnchor(container);
      if (isAnchor(state)) {
        reapplyAnchor(container, state);
        return;
      }
    }
    scrollToBottom();
  }, [reapplyAnchor, scrollToBottom, settledAnchor]);

  return (
    <ScrollContext.Provider
      value={{
        scrollRef,
        scrollToBottom,
        scrollToBottomIfNearBottom,
        anchorToMessage,
        keepAnchor,
        hasReadingAnchor,
        restoreReadingPosition,
      }}
    >
      {children}
    </ScrollContext.Provider>
  );
};
